/**
 * CSRF Protection Utility
 *
 * Simplified frontend support for fastapi-csrf-protect library.
 * Handles fetching and caching CSRF tokens from the backend.
 */

// Header name for CSRF token
export const CSRF_HEADER_NAME = "X-CSRF-Token";

// Storage key for CSRF token (in sessionStorage)
const CSRF_TOKEN_KEY = "nuclearn_csrf_token";
const CSRF_TOKEN_TIMESTAMP_KEY = "nuclearn_csrf_token_timestamp";

// Token max age matches backend setting (1 hour = 3600 seconds)
// Backend config: mre/utils/csrf.py -> CsrfSettings.max_age
// We refresh proactively at 80% of max age to avoid edge cases
const TOKEN_MAX_AGE_MS = 3600 * 1000;
const TOKEN_REFRESH_THRESHOLD_MS = TOKEN_MAX_AGE_MS * 0.8; // 48 minutes

// In-memory cache for CSRF token
let cachedToken: string | null = null;
let cachedTokenTimestamp: number | null = null;

// Promise to track ongoing token fetch (prevents duplicate requests)
let tokenFetchPromise: Promise<string> | null = null;

/**
 * Fetches a new CSRF token from the server.
 * @returns Promise resolving to the CSRF token
 */
async function fetchCSRFTokenFromServer(): Promise<string> {
  const apiUrl = window.ENV?.NUCLEARN_API_URL || "";

  if (!apiUrl) {
    throw new Error(
      "CSRF token fetch failed: NUCLEARN_API_URL is not configured",
    );
  }

  const csrfEndpoint = `${apiUrl}/auth/csrf-token`;
  console.log("[CSRF] Fetching token from:", csrfEndpoint);

  const response = await fetch(csrfEndpoint, {
    method: "GET",
    credentials: "include", // Important: include cookies in the request/response
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch CSRF token: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();

  if (!data.csrf_token) {
    throw new Error("CSRF token response missing csrf_token field");
  }

  console.log("[CSRF] Token fetched successfully");
  return data.csrf_token;
}

/**
 * Checks if the cached token is stale (approaching expiration).
 * Returns true if token should be refreshed proactively.
 *
 * Note: Uses client-side Date.now() which could be affected by clock skew.
 * Worst case: premature refresh (harmless) or delayed refresh (caught by retry logic).
 */
function isTokenStale(): boolean {
  const timestamp = cachedTokenTimestamp || getStoredTimestamp();
  if (!timestamp) {
    return true; // No timestamp means we should fetch fresh
  }
  const age = Date.now() - timestamp;
  return age >= TOKEN_REFRESH_THRESHOLD_MS;
}

/**
 * Gets the stored timestamp from sessionStorage.
 */
function getStoredTimestamp(): number | null {
  try {
    const stored = sessionStorage.getItem(CSRF_TOKEN_TIMESTAMP_KEY);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    return !isNaN(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Gets the current CSRF token.
 * Returns the stored token if available, otherwise returns empty string.
 * Use ensureCSRFToken() for async initialization.
 */
export function getCSRFToken(): string {
  if (cachedToken) {
    return cachedToken;
  }
  try {
    const token = sessionStorage.getItem(CSRF_TOKEN_KEY) || "";
    if (token) {
      // Load timestamp into memory cache as well
      cachedToken = token;
      cachedTokenTimestamp = getStoredTimestamp();
    }
    return token;
  } catch {
    return "";
  }
}

/**
 * Ensures a CSRF token is available, fetching from server if needed.
 * Also refreshes proactively if the token is approaching expiration.
 * @param forceRefresh - If true, forces a new token fetch even if cached
 * @returns Promise resolving to the CSRF token
 */
export async function ensureCSRFToken(forceRefresh = false): Promise<string> {
  // Return existing token if available (unless forcing refresh or stale)
  if (!forceRefresh) {
    const existingToken = getCSRFToken();
    if (existingToken) {
      // Check if token is approaching expiration
      if (isTokenStale()) {
        console.log(
          "[CSRF] Token is stale (approaching expiration), refreshing proactively",
        );
        // Start refresh if not already in progress
        if (!tokenFetchPromise) {
          tokenFetchPromise = fetchAndStoreToken().finally(() => {
            tokenFetchPromise = null;
          });
        }
        // Always wait for refresh when stale (fixes race condition where
        // concurrent requests would return stale token if promise existed)
        return tokenFetchPromise;
      }
      return existingToken;
    }
  } else {
    clearCSRFToken();
  }

  // If already fetching, wait for that promise
  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }

  // Fetch new token from server
  tokenFetchPromise = fetchAndStoreToken().finally(() => {
    tokenFetchPromise = null;
  });

  return tokenFetchPromise;
}

/**
 * Fetches token from server and stores it with timestamp.
 */
async function fetchAndStoreToken(): Promise<string> {
  const token = await fetchCSRFTokenFromServer();
  const timestamp = Date.now();

  cachedToken = token;
  cachedTokenTimestamp = timestamp;

  try {
    sessionStorage.setItem(CSRF_TOKEN_KEY, token);
    sessionStorage.setItem(CSRF_TOKEN_TIMESTAMP_KEY, timestamp.toString());
  } catch {
    // Ignore sessionStorage errors
  }

  return token;
}

/**
 * Clears the CSRF token from storage.
 * Should be called on logout.
 */
export function clearCSRFToken(): void {
  cachedToken = null;
  cachedTokenTimestamp = null;
  try {
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
    sessionStorage.removeItem(CSRF_TOKEN_TIMESTAMP_KEY);
  } catch {
    // Ignore errors when sessionStorage is unavailable
  }
}

/**
 * Regenerates the CSRF token by fetching a new one from the server.
 * Should be called after successful authentication to prevent token fixation.
 */
export async function regenerateCSRFToken(): Promise<string> {
  return ensureCSRFToken(true);
}

/**
 * List of auth endpoints that should include CSRF tokens.
 * These are endpoints accessed before the user has a JWT token.
 *
 * Note: Azure AD and OAuth endpoints are NOT included because they have
 * built-in CSRF protection via the OAuth state parameter.
 */
export const CSRF_PROTECTED_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/password/reset-request",
  "/auth/password/reset",
  "/auth/support-login",
  "/auth/validation-email/resend",
  "/user/exists",
];

/**
 * Checks if a URL should have CSRF protection applied.
 * @param url - The request URL
 * @returns True if the URL should have CSRF protection
 */
export function shouldApplyCSRFProtection(url: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    pathname = url.split("?")[0];
  }

  return CSRF_PROTECTED_ENDPOINTS.some(
    (endpoint) => pathname === endpoint || pathname.endsWith(endpoint),
  );
}
